import type { MultiFormatContentType } from '@atlassian/editor-plugin-ai-streaming-orchestrator/types';

import { log } from '../utils/aifc-logging';

import { isStreamable, type PartialStreamParser, type ParseResult } from './partial-stream-parser';

/**
 * V2 envelope shape emitted by {@link PartialStreamParserV2} for tool calls that
 * carry the HTML+ provisional metadata (`id`, `sequence`, `status`).
 *
 * v2 provisional snapshots are emitted by the backend only for
 * streamable commands — i.e. commands for which {@link isStreamable}
 * (in `partial-stream-parser.ts`) returns `true`: `replaceDoc`,
 * `replaceNode`, `insertNodeBefore`, `insertNodeAfter`, `appendNode`,
 * `appendNodeToEnd`. Non-streamable commands continue to flow through the
 * legacy v1 path with no provisional metadata.
 */
export type ToolCallV2Envelope = {
	/** Tool-call arguments (raw; the caller must shape per command). */
	arguments: Record<string, unknown>;
	/** Stable identifier for the tool call across all its snapshots. */
	id: string;
	/** Tool-call name (e.g. `'appendNodeToEnd'`). */
	name: string;
	/** Strictly monotonically increasing sequence number per `id`. */
	sequence: number;
	/**
	 * `'partial'` for every chunk except the last; `'complete'` for the last.
	 */
	status: 'partial' | 'complete';
};

/**
 * Per-id state needed to enforce the v2 contract:
 *
 * - Ignore stale or duplicate `sequence` numbers.
 * - Detect `status === 'complete'` so the processor knows to finish.
 *
 * NOTE: the `completed` boolean here refers to our local "we have seen a
 * `status === 'complete'` envelope" tracking. The corresponding envelope
 * field is literally `status: 'complete'` (not `'completed'`) — see
 * {@link ToolCallV2Envelope.status}.
 */
type V2State = {
	completed: boolean;
	latestSequence: number;
};

/**
 * Full v2 envelope shape check.
 */
function isV2Envelope(raw: unknown): raw is ToolCallV2Envelope {
	if (!raw || typeof raw !== 'object') {
		return false;
	}
	const obj = raw as Record<string, unknown>;
	return (
		typeof obj.id === 'string' &&
		obj.id.length > 0 &&
		((typeof obj.sequence === 'number' && Number.isFinite(obj.sequence)) ||
			(typeof obj.sequence === 'string' &&
				obj.sequence.length > 0 &&
				Number.isFinite(Number(obj.sequence)))) &&
		(obj.status === 'partial' || obj.status === 'complete')
	);
}

/**
 * Hybrid v1/v2 stream parser for HTML+ incremental tool-call streaming.
 *
 * Strategy:
 *
 * 1. Every chunk is accumulated into the internal buffer.
 * 2. Whenever the buffer parses as a valid JSON array (top-level `[...]`),
 *    we walk the array and split its elements into v2 envelopes (those that
 *    pass {@link isV2Envelope}) and v1 raw objects (everything else).
 * 3. V2 envelopes for streamable names ({@link isStreamable}) are queued for
 *    the processor to drain via {@link drainV2Envelopes}. V2 envelopes that
 *    arrive for non-streamable names (which the contract forbids but we
 *    tolerate for safety) are downgraded to the v1 path.
 * 4. V1 tool calls are forwarded to the original {@link PartialStreamParser}
 *    by re-serialising them, so today's behaviour (`InitAction` /
 *    `StreamAction` / `FinishAction`) is preserved exactly for non-streamable
 *    commands (`removeNode`, `setAttrs`, `moveNode`, `wrap`, …).
 *
 * The v2 spec guarantees each snapshot is a fully-closed JSON object inside
 * a closed JSON array, so the v2 path does not need progressive
 * unfinished-JSON repair. If the buffer is not yet a closed array we wait
 * for more input and try again on the next chunk.
 *
 * Stale / duplicate / out-of-order sequence numbers are filtered here so the
 * processor never observes them.
 */
export class PartialStreamParserV2 {
	private v2States: Map<string, V2State> = new Map();
	/** Queue of v2 envelopes that survived sequence-filtering and have not yet been drained. */
	private v2Queue: ToolCallV2Envelope[] = [];

	/**
	 * The v1 fall-through parser is INJECTED rather than owned. This lets the
	 * outer `DocumentUpdateCommandProcessor` share its own `PartialStreamParser`
	 * instance (and its mocking state in tests) with the v2 wrapper, so v1
	 * behaviour is observed in exactly one place per session.
	 */
	constructor(private v1Parser: PartialStreamParser) {}

	reset(): void {
		this.v2States.clear();
		this.v2Queue = [];
		// Note: we do not reset the injected v1Parser here — the owner
		// (`DocumentUpdateCommandProcessor`) calls `this.parser.reset()` itself.
	}

	/**
	 * Feed a new chunk of raw `toolCalls` text into the parser.
	 *
	 * Backend invariant: a v2 envelope is **never partially sent**. The HTML+
	 * streaming contract guarantees that every chunk carrying v2 metadata is a
	 * complete, closed JSON array all by itself. So we try to parse the chunk
	 * directly *before* falling back to any v1 buffering — that lets us handle
	 * the v2 happy path without any local buffer at all.
	 *
	 * Behaviour summary:
	 *
	 * - Try `tryParseClosedArray(chunk)` on the chunk alone first.
	 * - If it parses AND at least one element carries a v2 `id` → split
	 *   element-by-element into v2 envelopes (queued for {@link drainV2Envelopes})
	 *   and v1 raw objects (forwarded to the v1 parser as a re-serialised
	 *   array).
	 * - If it parses but no element carries an `id` → forward the **raw chunk
	 *   verbatim** to the v1 parser so v1 wire bytes stay byte-identical to
	 *   today (spec §5.4: "Gate ON, backend still emits v1-only … behaviour
	 *   byte-identical to today").
	 * - If it does NOT parse → forward the raw chunk verbatim to the injected
	 *   v1 parser. The v1 parser owns its own incremental buffer, so v1
	 *   mid-array fragments still stream correctly with no local accumulation
	 *   on our side.
	 *
	 * Returns whatever v1 {@link ParseResult}s the underlying parser emits.
	 */
	feed(chunk: string): ParseResult[] {
		const parsedArray = tryParseClosedArray(chunk);
		if (!parsedArray) {
			// Either a mid-v1-array fragment or otherwise not a complete JSON
			// array. Forward verbatim to the v1 parser which owns its own
			// incremental buffer.
			return this.v1Parser.parse(chunk);
		}

		// If no element strictly matches the v2 envelope shape
		// (id + sequence + status all present and well-typed), forward the
		// raw chunk verbatim so v1 wire bytes stay byte-identical to today.
		// Anything v2-shaped-but-malformed (e.g. carries `id` but missing
		// `sequence`) is treated as v1 wire bytes for the purposes of this
		// early-return: we forward verbatim and do NOT fire the
		// `v2_envelope_malformed` analytic, because we have no positive
		// signal the producer intended v2 for this chunk.
		const hasAnyV2Candidate = parsedArray.some(isV2Envelope);
		if (!hasAnyV2Candidate) {
			return this.v1Parser.parse(chunk);
		}

		// Mixed (or pure-v2) array: split element-by-element.
		const v1Raw: unknown[] = [];
		for (const item of parsedArray) {
			if (!isV2Envelope(item)) {
				// V2-candidate (has `id`) but missing/malformed `sequence`/`status`/
				// `name`. Spec §6.2: fire `contract_violation.v2_envelope_malformed`
				// and downgrade to v1.
				log('htmleditor.contract_violation.v2_envelope_malformed', {
					streamingContract: 'v2',
					reason: 'malformed_envelope',
					...safeMetadata(item),
				});
				v1Raw.push(item);
				continue;
			}

			if (!isStreamable(item.name)) {
				// Backend invariant violation: v2 metadata on a non-streamable name.
				// Spec §6.2: fire same `contract_violation.v2_envelope_malformed`
				// and downgrade to v1.
				log('htmleditor.contract_violation.v2_envelope_malformed', {
					streamingContract: 'v2',
					name: item.name,
					reason: 'non_streamable_name',
				});
				v1Raw.push(item);
				continue;
			}

			// Normalise `sequence` to a number immediately after the type
			// guard so every downstream usage (comparison, state storage,
			// analytics, queue payload) treats it as a true number. The
			// type guard `isV2Envelope` admits a numeric string for
			// `sequence` (some backend serialisers emit large integers as
			// strings to avoid JS Number precision loss), but storing or
			// comparing a string would trigger JS lexicographic ordering
			// (e.g. `"10" <= "9"` is `true`), silently dropping every
			// envelope once the sequence exceeds single digits. Convert
			// once here and mutate the envelope so consumers of the v2
			// queue (`ToolCallProcessorV2.apply`) also see a number.
			const sequenceAsNumber =
				typeof item.sequence === 'number' ? item.sequence : Number(item.sequence);
			item.sequence = sequenceAsNumber;

			const state = this.v2States.get(item.id) ?? { latestSequence: -1, completed: false };

			if (state.completed) {
				// Spec §2.2: `partial`/`complete` after `complete` on same id —
				// drop and fire `contract_violation.partial_after_complete`.
				log('htmleditor.contract_violation.partial_after_complete', {
					streamingContract: 'v2',
					id: item.id,
					sequence: sequenceAsNumber,
					status: item.status,
				});
				continue;
			}

			if (sequenceAsNumber <= state.latestSequence) {
				// Stale / duplicate sequence. Spec §6.2: fire `v2_stale_sequence`.
				log('htmleditor.v2_stale_sequence', {
					streamingContract: 'v2',
					id: item.id,
					receivedSequence: sequenceAsNumber,
					latestSequence: state.latestSequence,
				});
				continue;
			}

			state.latestSequence = sequenceAsNumber;
			if (item.status === 'complete') {
				state.completed = true;
			}
			this.v2States.set(item.id, state);
			this.v2Queue.push(item);
		}

		// Forward the v1 portion of the array (including any downgraded
		// envelopes) to the legacy parser by re-serialising it back into the
		// shape the legacy parser expects.
		let v1Results: ParseResult[] = [];
		if (v1Raw.length > 0) {
			v1Results = this.v1Parser.parse(JSON.stringify(v1Raw));
		}

		return v1Results;
	}

	/** Pull and clear the queued v2 envelopes for the processor to dispatch. */
	drainV2Envelopes(): ToolCallV2Envelope[] {
		if (this.v2Queue.length === 0) {
			return [];
		}
		const out = this.v2Queue;
		this.v2Queue = [];
		return out;
	}

}

/**
 * Try to parse `input` as a complete JSON array. Returns the parsed array on
 * success, or `null` if `input` is not yet a complete JSON array. The caller
 * is responsible for inspecting elements.
 *
 * Free function (rather than a method) because {@link PartialStreamParserV2}
 * no longer owns an accumulation buffer — v2 envelopes always arrive as one
 * complete closed JSON array per chunk, so we parse the chunk directly.
 */
function tryParseClosedArray(input: string): unknown[] | null {
	const trimmed = input.trim();
	if (!trimmed.startsWith('[') || !trimmed.endsWith(']')) {
		return null;
	}
	let parsed: unknown;
	try {
		parsed = JSON.parse(trimmed);
	} catch {
		return null;
	}
	if (!Array.isArray(parsed)) {
		return null;
	}
	return parsed;
}

/**
 * Allow-list extractor for analytics on malformed v2 envelopes.
 *
 * We never serialise the raw `item` (or any `arguments.value` it may
 * carry) because a malformed envelope can still contain a partially-written
 * ADF document fragment that constitutes user-generated content (UGC).
 * Only well-typed v2 metadata fields are emitted; everything else is
 * dropped on the floor. See HOT-120175 / `analytics-review` skill for the
 * class of leak this prevents.
 */
function safeMetadata(value: unknown): {
	id?: string;
	name?: string;
	sequence?: number;
	status?: string;
} {
	if (!value || typeof value !== 'object') {
		return {};
	}
	const obj = value as Record<string, unknown>;
	const out: { id?: string; name?: string; sequence?: number; status?: string } = {};
	if (typeof obj.id === 'string') {
		out.id = obj.id;
	}
	if (typeof obj.name === 'string') {
		out.name = obj.name;
	}
	if (typeof obj.sequence === 'number' && Number.isFinite(obj.sequence)) {
		out.sequence = obj.sequence;
	} else if (
		typeof obj.sequence === 'string' &&
		obj.sequence.length > 0 &&
		Number.isFinite(Number(obj.sequence))
	) {
		out.sequence = Number(obj.sequence);
	}
	if (typeof obj.status === 'string') {
		out.status = obj.status;
	}
	return out;
}

/**
 * Convenience: extract the `contentType` from a v2 envelope's arguments. The
 * v2 streamable subset always carries `arguments.contentType` per the
 * contract; default to `'application/adf+json'` when missing.
 */
export function getEnvelopeContentType(envelope: ToolCallV2Envelope): MultiFormatContentType {
	const ct = envelope.arguments?.contentType;
	if (ct === 'text/html' || ct === 'text/markdown' || ct === 'application/adf+json') {
		return ct;
	}
	return 'application/adf+json';
}
